import { memo, useCallback, useMemo, useRef, useState } from "react"
import { useClickOutside } from "../../../hooks/useClickOutside"
import { KEY } from "../../../data/variables"
import Input from ".."
import Icon from "../../Icon"
import { ICON } from "../../Icon/data"
import DropDownSelection from "../../DropdownSelection"
import { InputConfig } from "../types"

type Select = {
  id: string
  value: string | number
  options: Record<string | number, string>
  onChange: (val?: string | number) => void
  searchable?: boolean
  config?: InputConfig
}

const Select = memo(({
  value, options = {}, onChange, searchable = false, config
}: Select) => {
  const dropdownRef = useRef()
  const inputRef = useRef()

  const [search, setSearch] = useState<string>('')
  const [isOpen, setIsOpen] = useState<boolean>(false)
  
  useClickOutside(dropdownRef, () => setIsOpen(false))

  const addValue = useCallback((key?: string | number) => {
    onChange(key)
    setSearch('')
  }, [onChange])

  const onKeyDown = useCallback((e: KeyboardEvent, id: string | number) => {
    if (e.key === KEY.ENTER) {
      addValue(id)
    }
  }, [addValue])

  const filteredOptions = useMemo(() => {
    return Object.keys(options).reduce((arr, key) => {
      if (value !== key) {
        const label = options[key]
        if (search?.length) {
          if (label.toLowerCase().includes(search.toLowerCase())) {
            const temp = {
              label,
              onClick: () => addValue(key),
              onKeyDown: (e: KeyboardEvent) => onKeyDown(e, key)
            }
            arr.push(temp)
          }
        } else {
          const temp = {
            label,
            onClick: () => addValue(key),
            onKeyDown: (e) => onKeyDown(e, key)
          }
          arr.push(temp)
        }
      }
      return arr
    }, [])
  }, [options, value, addValue, onKeyDown, search])

  return (
    <div className="Select" ref={inputRef}>
      <div
        className="Select__search"
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(true)}
      >
        {searchable && !value ? (
          <Input
            type="text"
            value={search}
            onInput={(val) => setSearch(val)}
            onFocus={() => setIsOpen(true)}
          />
        ) : (
          <div className="Select__search-value">
            {options[value] ?? null}
          </div>
        )}
        <div className="Select__search-icons">
          {value && searchable && (
            <div
              className="Select__search-icons__remove"
              role="button"
              tabIndex={0}
              onClick={() => onChange(undefined)}
            >
              <Icon icon={ICON.CLOSE} size={16} />
            </div>
          )}
          <Icon icon={isOpen ? ICON.CHEVRON_UP : ICON.CHEVRON_DOWN} size={16} />
        </div>
      </div>
      {isOpen && (
        <div ref={dropdownRef} className="Select__dropdown">
          <DropDownSelection
            options={filteredOptions}
            boundsRef={inputRef}
            align={config?.dropdownAlignment ?? 'right'}
            width={config?.dropdownWidth ?? 160}
          />
        </div>
      )}
    </div>
  )
})

export default Select
